import { useEffect, useMemo, useRef, useState } from "react";
import type { IControl, Map as MapboxMap } from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import type * as GeoJSON from "geojson";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Slider,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import PentagonOutlinedIcon from "@mui/icons-material/PentagonOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";

import { FreehandFeatureProperties, LineStyleKey } from "../../types/storyMap";
import {
  FREEHAND_DEFAULT_COLOR,
  FREEHAND_DEFAULT_FILL_OPACITY,
  FREEHAND_DEFAULT_WIDTH,
  isHexColor,
} from "./layerStyles";
import { ColorField, LineStyleToggle } from "./LayerStyleFields";

/**
 * Freehand annotation editor for one "freehand" layer entry: mounts a
 * mapbox-gl-draw control on the builder map, seeds it with the entry's
 * features, and writes every create/edit/delete straight back to the entry
 * (features carry FreehandFeatureProperties). The floating toolbar picks the
 * draw mode; the style panel edits the selected features, or — with nothing
 * selected — the style new features start with.
 *
 * Rendering while drawing is Draw's own (styles below read the `user_*`
 * copies of the feature properties); the entry's normal story layers are
 * hidden by the builder for the duration so nothing draws twice.
 */

type DrawMode = "simple_select" | "direct_select" | "draw_line_string" | "draw_polygon";

const TEAL = "#00d4ad";

const COLOR_EXPR = ["coalesce", ["get", "user_color"], FREEHAND_DEFAULT_COLOR];
const WIDTH_EXPR = ["coalesce", ["get", "user_lineWidth"], FREEHAND_DEFAULT_WIDTH];
const FILL_OPACITY_EXPR = [
  "coalesce",
  ["get", "user_fillOpacity"],
  FREEHAND_DEFAULT_FILL_OPACITY,
];
const IS_FEATURE = ["==", "meta", "feature"];
const IS_LINEAR = ["any", ["==", "$type", "LineString"], ["==", "$type", "Polygon"]];
const STYLE_FILTERS: Record<LineStyleKey, unknown[]> = {
  solid: ["any", ["!has", "user_lineStyle"], ["==", "user_lineStyle", "solid"]],
  dashed: ["==", "user_lineStyle", "dashed"],
  dotted: ["==", "user_lineStyle", "dotted"],
};
const DASHES: Record<LineStyleKey, number[] | null> = {
  solid: null,
  dashed: [2, 2],
  dotted: [0, 2],
};

/** Draw styles: per-feature colour/width/dash, teal vertices, white halo when active. */
const DRAW_STYLES: object[] = [
  {
    id: "story-fh-active-halo",
    type: "line",
    filter: ["all", IS_FEATURE, IS_LINEAR, ["==", "active", "true"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": "#ffffff", "line-width": ["+", WIDTH_EXPR, 5], "line-opacity": 0.55 },
  },
  {
    id: "story-fh-fill",
    type: "fill",
    filter: ["all", IS_FEATURE, ["==", "$type", "Polygon"]],
    paint: { "fill-color": COLOR_EXPR, "fill-opacity": FILL_OPACITY_EXPR },
  },
  ...(["solid", "dashed", "dotted"] as LineStyleKey[]).map((style) => ({
    id: `story-fh-line-${style}`,
    type: "line",
    filter: ["all", IS_FEATURE, IS_LINEAR, STYLE_FILTERS[style]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": COLOR_EXPR,
      "line-width": WIDTH_EXPR,
      ...(DASHES[style] ? { "line-dasharray": DASHES[style] } : {}),
    },
  })),
  {
    id: "story-fh-midpoint",
    type: "circle",
    filter: ["all", ["==", "meta", "midpoint"], ["==", "$type", "Point"]],
    paint: { "circle-radius": 4, "circle-color": "#ffd600", "circle-stroke-width": 1, "circle-stroke-color": "#212b37" },
  },
  {
    id: "story-fh-vertex-halo",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
    paint: { "circle-radius": 7, "circle-color": "#ffffff" },
  },
  {
    id: "story-fh-vertex",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
    paint: {
      "circle-radius": 5,
      "circle-color": ["case", ["==", ["get", "active"], "true"], "#ffd600", TEAL],
    },
  },
];

interface DrawStyle {
  color: string;
  lineStyle: LineStyleKey;
  lineWidth: number;
  fillOpacity: number;
  label: string;
}

const DEFAULT_STYLE: DrawStyle = {
  color: FREEHAND_DEFAULT_COLOR,
  lineStyle: "solid",
  lineWidth: FREEHAND_DEFAULT_WIDTH,
  fillOpacity: FREEHAND_DEFAULT_FILL_OPACITY,
  label: "",
};

function styleFromProps(props: FreehandFeatureProperties | null | undefined): DrawStyle {
  return {
    color: isHexColor(props?.color) ? props.color : DEFAULT_STYLE.color,
    lineStyle: props?.lineStyle ?? DEFAULT_STYLE.lineStyle,
    lineWidth: props?.lineWidth ?? DEFAULT_STYLE.lineWidth,
    fillOpacity: props?.fillOpacity ?? DEFAULT_STYLE.fillOpacity,
    label: props?.label ?? "",
  };
}

/** Feature properties for a style (label omitted when blank). */
function propsFromStyle(style: DrawStyle): FreehandFeatureProperties {
  const props: FreehandFeatureProperties = {
    color: style.color,
    lineStyle: style.lineStyle,
    lineWidth: style.lineWidth,
    fillOpacity: style.fillOpacity,
  };
  if (style.label.trim()) props.label = style.label.trim();
  return props;
}

/** Ensure every seeded feature has a string id (Draw keys on it). */
function withIds(fc: GeoJSON.FeatureCollection): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: fc.features.map((f) => ({
      ...f,
      id: f.id != null ? String(f.id) : crypto.randomUUID(),
    })),
  };
}

interface FreehandDrawToolProps {
  /** The builder map (StoryMapCanvasHandle.getMap()), resolved on mount. */
  getMap: () => MapboxMap | null;
  features: GeoJSON.FeatureCollection | undefined;
  onChange: (features: GeoJSON.FeatureCollection) => void;
  onDone: () => void;
}

export default function FreehandDrawTool({
  getMap,
  features,
  onChange,
  onDone,
}: FreehandDrawToolProps) {
  const drawRef = useRef<MapboxDraw | null>(null);
  const [mode, setMode] = useState<DrawMode>("simple_select");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Style new features start with (last style set with nothing selected).
  const [nextStyle, setNextStyle] = useState<DrawStyle>(DEFAULT_STYLE);
  const nextStyleRef = useRef(nextStyle);
  nextStyleRef.current = nextStyle;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [featureCount, setFeatureCount] = useState(features?.features.length ?? 0);
  // Re-read selected features' props after each edit to refresh the panel.
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const map = getMap();
    if (!map) return;
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      userProperties: true,
      styles: DRAW_STYLES as never,
      defaultMode: "simple_select",
    });
    map.addControl(draw as unknown as IControl);
    drawRef.current = draw;
    const seeded = withIds(
      features ?? { type: "FeatureCollection", features: [] }
    );
    draw.set(seeded as never);

    const emit = () => {
      const all = draw.getAll() as unknown as GeoJSON.FeatureCollection;
      setFeatureCount(all.features.length);
      setRevision((r) => r + 1);
      onChangeRef.current(all);
    };
    const onCreate = (e: { features: GeoJSON.Feature[] }) => {
      // Stamp the "next" style on freshly drawn features.
      const props = propsFromStyle(nextStyleRef.current);
      e.features.forEach((f) => {
        if (f.id == null) return;
        (Object.keys(props) as (keyof FreehandFeatureProperties)[]).forEach((k) => {
          draw.setFeatureProperty(String(f.id), k, props[k]);
        });
      });
      emit();
    };
    const onUpdate = () => emit();
    const onDelete = () => emit();
    const onSelection = (e: { features: GeoJSON.Feature[] }) => {
      setSelectedIds(e.features.map((f) => String(f.id)));
    };
    const onModeChange = (e: { mode: DrawMode }) => setMode(e.mode);
    map.on("draw.create", onCreate);
    map.on("draw.update", onUpdate);
    map.on("draw.delete", onDelete);
    map.on("draw.selectionchange", onSelection);
    map.on("draw.modechange", onModeChange);
    return () => {
      map.off("draw.create", onCreate);
      map.off("draw.update", onUpdate);
      map.off("draw.delete", onDelete);
      map.off("draw.selectionchange", onSelection);
      map.off("draw.modechange", onModeChange);
      try {
        map.removeControl(draw as unknown as IControl);
      } catch {
        // Map already torn down.
      }
      drawRef.current = null;
    };
    // Seed once per mount: the entry's features are ours to write afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeMode = (next: DrawMode) => {
    const draw = drawRef.current;
    if (!draw) return;
    draw.changeMode(next as never);
    setMode(next);
  };

  const deleteSelected = () => {
    const draw = drawRef.current;
    if (!draw || selectedIds.length === 0) return;
    draw.delete(selectedIds);
    setSelectedIds([]);
    const all = draw.getAll() as unknown as GeoJSON.FeatureCollection;
    setFeatureCount(all.features.length);
    onChangeRef.current(all);
  };

  // Style panel state: the first selected feature's props, else nextStyle.
  const selectedStyle = useMemo<DrawStyle>(() => {
    const draw = drawRef.current;
    const first = selectedIds[0];
    if (!draw || !first) return nextStyle;
    const feature = draw.get(first) as GeoJSON.Feature | undefined;
    return styleFromProps(feature?.properties as FreehandFeatureProperties);
    // revision: props change without selection changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, nextStyle, revision]);
  const selectedHasPolygon = useMemo(() => {
    const draw = drawRef.current;
    if (!draw) return false;
    return selectedIds.some((id) => {
      const f = draw.get(id) as GeoJSON.Feature | undefined;
      return f?.geometry?.type === "Polygon" || f?.geometry?.type === "MultiPolygon";
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, revision]);

  const patchStyle = (patch: Partial<DrawStyle>) => {
    const draw = drawRef.current;
    if (selectedIds.length === 0 || !draw) {
      setNextStyle((s) => ({ ...s, ...patch }));
      return;
    }
    // Apply to every selected feature; Draw re-renders on setFeatureProperty.
    selectedIds.forEach((id) => {
      const current = styleFromProps(
        (draw.get(id) as GeoJSON.Feature | undefined)?.properties as FreehandFeatureProperties
      );
      const props = propsFromStyle({ ...current, ...patch });
      draw.setFeatureProperty(id, "color", props.color);
      draw.setFeatureProperty(id, "lineStyle", props.lineStyle);
      draw.setFeatureProperty(id, "lineWidth", props.lineWidth);
      draw.setFeatureProperty(id, "fillOpacity", props.fillOpacity);
      draw.setFeatureProperty(id, "label", props.label ?? "");
    });
    // Also remember it as the style for the next feature.
    setNextStyle((s) => ({ ...s, ...patch, label: s.label }));
    setRevision((r) => r + 1);
    onChangeRef.current(draw.getAll() as unknown as GeoJSON.FeatureCollection);
  };

  const editingLabel = selectedIds.length > 0;
  const showFillOpacity = mode === "draw_polygon" || selectedHasPolygon;

  const toolButton = (
    title: string,
    icon: React.ReactNode,
    active: boolean,
    onClick: () => void,
    disabled = false
  ) => (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            color: active ? TEAL : "white",
            bgcolor: active ? "rgba(0,212,173,0.15)" : "transparent",
            border: active ? `1px solid ${TEAL}` : "1px solid transparent",
            borderRadius: 1,
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Paper
      elevation={6}
      sx={{
        position: "absolute",
        top: 12,
        left: 12,
        zIndex: 4,
        width: 292,
        bgcolor: "rgba(15, 23, 42, 0.94)",
        color: "white",
        borderRadius: 2,
        p: 1.25,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {toolButton("Draw a line", <TimelineIcon fontSize="small" />, mode === "draw_line_string", () =>
          changeMode("draw_line_string")
        )}
        {toolButton(
          "Draw a polygon",
          <PentagonOutlinedIcon fontSize="small" />,
          mode === "draw_polygon",
          () => changeMode("draw_polygon")
        )}
        {toolButton(
          "Select / move",
          <NearMeOutlinedIcon fontSize="small" />,
          mode === "simple_select" || mode === "direct_select",
          () => changeMode("simple_select")
        )}
        {toolButton(
          "Delete selected",
          <DeleteOutlineIcon fontSize="small" />,
          false,
          deleteSelected,
          selectedIds.length === 0
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="contained"
          color="secondary"
          startIcon={<CheckIcon />}
          onClick={onDone}
          sx={{ textTransform: "none" }}
        >
          Done
        </Button>
      </Box>
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.3 }}>
        {mode === "draw_line_string" || mode === "draw_polygon"
          ? "Click to add points, double-click (or Enter) to finish. Esc cancels."
          : selectedIds.length > 0
          ? `${selectedIds.length} selected — drag to move, click a vertex to reshape. Style edits apply to the selection.`
          : `${featureCount} feature${featureCount === 1 ? "" : "s"}. Click a feature to select it, or pick a tool to draw.`}
      </Typography>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          // The shared fields are built for light dialogs; lift their text.
          "& .MuiTypography-caption": { color: "rgba(255,255,255,0.75)" },
          "& .MuiToggleButton-root": { color: "white", borderColor: "rgba(255,255,255,0.25)" },
          "& .MuiToggleButton-root.Mui-selected": { bgcolor: "rgba(0,212,173,0.18)" },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: "white !important" }}>
          {editingLabel ? "Selected feature style" : "Style for new features"}
        </Typography>
        <ColorField
          label="Colour"
          value={selectedStyle.color}
          fallback={FREEHAND_DEFAULT_COLOR}
          allowClear={false}
          onChange={(color) => color && patchStyle({ color })}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="caption">Line style</Typography>
          <LineStyleToggle
            value={selectedStyle.lineStyle}
            onChange={(lineStyle) => patchStyle({ lineStyle })}
            color={selectedStyle.color}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="caption" sx={{ minWidth: 64 }}>
            Width
          </Typography>
          <Slider
            size="small"
            value={selectedStyle.lineWidth}
            min={1}
            max={12}
            step={0.5}
            onChange={(_, v) => patchStyle({ lineWidth: Array.isArray(v) ? v[0] : v })}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v} px`}
            sx={{ flex: 1, color: TEAL }}
          />
          <Typography variant="caption" sx={{ minWidth: 34, textAlign: "right" }}>
            {selectedStyle.lineWidth} px
          </Typography>
        </Box>
        {showFillOpacity && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="caption" sx={{ minWidth: 64 }}>
              Fill
            </Typography>
            <Slider
              size="small"
              value={selectedStyle.fillOpacity}
              min={0}
              max={1}
              step={0.05}
              onChange={(_, v) => patchStyle({ fillOpacity: Array.isArray(v) ? v[0] : v })}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              sx={{ flex: 1, color: TEAL }}
            />
            <Typography variant="caption" sx={{ minWidth: 34, textAlign: "right" }}>
              {Math.round(selectedStyle.fillOpacity * 100)}%
            </Typography>
          </Box>
        )}
        {editingLabel && (
          <TextField
            size="small"
            label="Legend label"
            placeholder="e.g. Proposed firebreak"
            value={selectedStyle.label}
            onChange={(e) => patchStyle({ label: e.target.value })}
            InputLabelProps={{ sx: { color: "rgba(255,255,255,0.7)" } }}
            sx={{
              "& .MuiInputBase-root": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
            }}
          />
        )}
      </Box>
    </Paper>
  );
}
